import ComercialInterfaceHeader from "@/components/comercial/ComercialInterfaceHeader";
import ComercialTable from "@/components/comercial/ComercialTable";
import ComercialDrawer from "@/components/general/ComercialDrawer";
import useManagerStores from "@/hooks/context-hooks/useManagerStores";
import useFetchManagerStores from "@/hooks/entities/useFetchManagerStores";
import useFetchUserExcursionFromStoreId from "@/hooks/entities/userFetchUserExcursionFromStoreId";
import { sharedStyles } from "@/styles/shared";
import { priceFormatter } from "@/utils/priceFormatter";
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import excursionDefaultImage from "@/assets/svg/defaultExcursion.svg";
import useFilterUserExcursionsByDate, {
  DATE_FILTER_OPTIONS,
} from "./functions";
import { formatDateString } from "@/utils/stringUtils";

const Dashboard = () => {
  const navigate = useNavigate();

  const managerStoresContext = useManagerStores();
  const { stores } = managerStoresContext;
  const { loadingManagerStores } = useFetchManagerStores();

  const [selectedStoreIndex, setSelectedStoreIndex] = useState(0);

  const { userExcursions } = useFetchUserExcursionFromStoreId(
    stores && stores[selectedStoreIndex]?.id
  );

  const { filteredUserExcursions, dateFilterIndex, setDateFilterIndex } =
    useFilterUserExcursionsByDate(userExcursions);

  const cartTableHeader = (
    <TableRow>
      <TableCell align="left">Excursão</TableCell>
      <TableCell align="right">Preço</TableCell>
    </TableRow>
  );

  const cartTableBody = (
    <>
      {filteredUserExcursions?.map((item) => (
        <TableRow
          key={item.id}
          sx={{
            "&:last-child td, &:last-child th": { border: 0 },
            display: { xs: "column", md: "row", lg: "row" },
          }}
        >
          <TableCell
            component="th"
            sx={{ ...sharedStyles.horizontalContent }}
            align="left"
          >
            <Avatar
              src={item.Excursion.imagePreview[0] || excursionDefaultImage}
              sx={sharedStyles.tableAvatarStyle}
            />

            <ListItemText
              primary={`${item.Excursion.title}`}
              secondary={`adquirido por ${item?.User?.name ?? "carregando..."} em ${formatDateString(item.createdAt)}`}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </TableCell>

          <TableCell align="right">
            <ListItemText
              primary={`R$ ${priceFormatter(item.totalValue)}`}
              secondary={`${item.quantity + 1} ${item.quantity + 1 !== 1 ? " vagas" : "vaga"}`}
              primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
            />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const storeSelector = (
    <FormControl fullWidth sx={{ maxWidth: "150px", marginTop: 4 }}>
      <InputLabel>Agência</InputLabel>
      <Select
        label="Agência"
        value={stores && selectedStoreIndex}
        onChange={(e: any) => setSelectedStoreIndex(e.target.value as number)}
        size="small"
      >
        {stores &&
          stores.map((store, index) => (
            <MenuItem value={index} key={index}>
              {store?.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );

  const dateFilterSelector = (
    <FormControl fullWidth sx={{ maxWidth: "150px", marginTop: 4 }}>
      <InputLabel>Filtrar por data</InputLabel>
      <Select
        label="Filtrar por data"
        value={dateFilterIndex}
        onChange={(e: any) => setDateFilterIndex(e.target.value as number)}
        size="small"
      >
        {DATE_FILTER_OPTIONS.map((option, index) => (
          <MenuItem value={index} key={index}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderExcursionsPageContent = (
    <>
      {!loadingManagerStores ? (
        <>
          {stores && stores.length > 0 ? (
            <Box
              sx={{
                ...sharedStyles.verticalContent,
                width: "100%",
                minHeight: "80vh",
                gap: 0,
              }}
            >
              <ComercialInterfaceHeader
                title={`Excursões adquiridas - ${
                  stores[selectedStoreIndex]?.name ?? "Carregando..."
                }`}
              />

              <Box
                sx={{
                  ...sharedStyles.horizontalContent,
                  justifyContent: "space-between",
                }}
              >
                {storeSelector}
                {dateFilterSelector}
              </Box>

              {filteredUserExcursions?.length > 0 ? (
                <ComercialTable header={cartTableHeader} body={cartTableBody} />
              ) : (
                <Typography
                  sx={{ alignSelf: "center", marginTop: 5 }}
                  color="text.secondary"
                >
                  Nenhuma excursão de adquirida
                </Typography>
              )}
            </Box>
          ) : (
            <Typography textAlign="justify">
              Nenhuma agência cadastrada.{" "}
              <Typography
                color="primary"
                fontWeight="bold"
                onClick={() => navigate("/agencies/add")}
                sx={{ cursor: "pointer" }}
                variant={"span"}
              >
                Cadastrar uma agência
              </Typography>
              .
            </Typography>
          )}
        </>
      ) : (
        <Box sx={sharedStyles.loadingContainer}>
          <CircularProgress />
        </Box>
      )}
    </>
  );

  return <ComercialDrawer content={renderExcursionsPageContent} />;
};

export default Dashboard;
